/**
 * designTokens.js — FONTE ÚNICA da verdade dos tokens de design.
 *
 * Tudo que é "valor de design" (cor, tamanho, raio, sombra, espaçamento) nasce AQUI.
 * A partir deste módulo:
 *   1) o tema MUI é montado (App.jsx consome `muiPalette` / `muiTypography` / `shape`);
 *   2) o `src/styles/tokens.css` (:root, o bridge) é GERADO por `scripts/generate-tokens.mjs`
 *      (rodado no prebuild/predev) e a MESMA saída é copiada pro pacote do Claude Design.
 *
 * ⚠️ NUNCA edite `tokens.css` na mão — ele é gerado. Edite os valores AQUI.
 * Custom tokens (WhatsApp, slate, softTints) vêm de brandColors.js / uiTokens.js (reexportados).
 */
import { brandColors } from '../constants/brandColors.js';
import { softTints, slate } from '../constants/uiTokens.js';

// ── Palette (semântica) — TEMA PADRÃO do designer (= themes/templates/default.json).
// Estes são os valores DEFAULT/fallback estático; o ThemeApplier sobrescreve por tenant
// em runtime com os MESMOS nomes de var. Mudou o tema Padrão? Atualizar AQUI e no default.json.
export const palette = {
  primary: '#4C5FD5',
  primaryDark: '#3A49B0',
  primaryLight: '#8290EE',
  primarySoft: '#EEF0FE',   // fundo suave da primária (chips/hover/selected)
  onPrimary: '#FFFFFF',     // texto/ícone sobre a primária
  drawerAccent: '#4C5FD5',  // tom de marca legível sobre drawer ESCURO (default = primária)
  secondary: '#E0457B',
  success: '#1F9D6B',
  warning: '#E08A2B',
  error: '#E04A3C',
  info: '#2E86D8',
};

// ── Texto / linhas / superfícies ─────────────────────────────────────
export const surfaces = {
  textPrimary: '#1B2130',
  textSecondary: '#5E6B85',
  divider: '#E6E9F1',
  bgPage: '#F5F7FB',
  bgDrawer: '#FFFFFF',
  // Texto/ícones DO MENU (sobre bg-drawer). Existe porque temas de drawer ESCURO
  // (tattoo-studio, personal-trainer, teacher) têm text global escuro → menu ilegível
  // se o drawer herdar text-primary. Default = mesmo do texto global (drawer claro).
  textDrawer: '#1B2130',
  surface: '#FFFFFF',
};

// ── Forma + elevação + espaçamento ───────────────────────────────────
export const shape = {
  radiusCard: 14,
  radiusCardMobile: 8,
  radiusSm: 9,
  radiusLg: 20,
  shadowCard: '0 1px 2px rgba(20,28,54,0.06), 0 1px 1px rgba(20,28,54,0.04)',
  shadowHover: '0 10px 28px rgba(20,28,54,0.12)',
};
export const spacingBase = 8;

// ── Tipografia (DM Sans corpo / Plus Jakarta Sans títulos) — escala única (docs/DESIGN-FLOW.md secao 4) ──
export const fontFamily = "'DM Sans', 'Segoe UI', system-ui, -apple-system, sans-serif";
export const fontHeading = "'Plus Jakarta Sans', 'DM Sans', system-ui, sans-serif";

// Objeto de tipografia EXATO consumido pelo createTheme (desktop + @media mobile).
// fontFamily = var(--font-family) p/ que TODA variante (h1..body1) nasça com a var e o ThemeApplier
// (Fase B) resolva pra fonte do tenant em runtime. A var em si (fallback) é emitida por toCssVars().
export const muiTypography = {
  fontFamily: 'var(--font-family)',
  h1: { fontWeight: 700, fontSize: '1.75rem', lineHeight: 1.2, '@media (max-width:600px)': { fontSize: '1.5rem' } },
  h2: { fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.25, '@media (max-width:600px)': { fontSize: '1.375rem' } },
  h3: { fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.3, '@media (max-width:600px)': { fontSize: '1.125rem' } },
  h4: { fontWeight: 600, fontSize: '1.5rem', '@media (max-width:600px)': { fontSize: '1.25rem' } },
  h5: { fontWeight: 600, fontSize: '1.25rem', '@media (max-width:600px)': { fontSize: '1.125rem' } },
  h6: { fontWeight: 600, fontSize: '1.0625rem', '@media (max-width:600px)': { fontSize: '0.9375rem' } },
  subtitle1: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.4, '@media (max-width:600px)': { fontSize: '0.9375rem' } },
  subtitle2: { fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.4, '@media (max-width:600px)': { fontSize: '0.8125rem' } },
  body1: { fontSize: '1rem', '@media (max-width:600px)': { fontSize: '0.9375rem' } },
  body2: { fontSize: '0.875rem', '@media (max-width:600px)': { fontSize: '0.8125rem' } },
  button: { textTransform: 'none', fontSize: '0.875rem', '@media (max-width:600px)': { fontSize: '0.8125rem' } },
  caption: { fontSize: '0.75rem', lineHeight: 1.4, '@media (max-width:600px)': { fontSize: '0.6875rem' } },
  overline: { fontWeight: 600, fontSize: '0.6875rem', lineHeight: 1.4, letterSpacing: '0.04em', textTransform: 'uppercase' },
};

// Tamanhos desktop expostos como CSS var (--fs-*). Nomes = variantes da escala.
const fontSizeVars = {
  h1: '1.75rem', h2: '1.5rem', h3: '1.25rem', h4: '1.5rem', h5: '1.25rem', h6: '1.0625rem',
  subtitle1: '1rem', subtitle2: '0.875rem', body1: '1rem', body2: '0.875rem',
  caption: '0.75rem', overline: '0.6875rem',
};

// ── Objeto de palette EXATO pro createTheme ──────────────────────────
export const muiPalette = {
  primary: { main: palette.primary },
  secondary: { main: palette.secondary },
  text: { primary: surfaces.textPrimary, secondary: surfaces.textSecondary },
  divider: surfaces.divider,
};

/**
 * Gera o corpo do :root (CSS custom properties) a partir dos valores acima.
 * Usado por scripts/generate-tokens.mjs. Não formata nada à mão em outro lugar.
 */
export function toCssVars() {
  const L = [];
  const g = (name, val, comment) => L.push(`  --${name}: ${val};${comment ? `   /* ${comment} */` : ''}`);
  const sec = (t) => L.push(`\n  /* ── ${t} ── */`);

  sec('Brand / semantic palette (theme.palette.*)');
  g('color-primary', palette.primary, 'primary.main');
  g('color-primary-dark', palette.primaryDark);
  g('color-primary-light', palette.primaryLight);
  g('color-primary-soft', palette.primarySoft, 'fundo suave da primária');
  g('on-primary', palette.onPrimary, 'texto/ícone sobre a primária');
  g('drawer-accent', palette.drawerAccent, 'marca legível sobre bg-drawer escuro (default = primária)');
  g('color-secondary', palette.secondary);
  g('color-success', palette.success);
  g('color-warning', palette.warning);
  g('color-error', palette.error);
  g('color-info', palette.info);

  sec('Text + lines + surfaces');
  g('text-primary', surfaces.textPrimary);
  g('text-secondary', surfaces.textSecondary);
  g('divider', surfaces.divider);
  g('bg-page', surfaces.bgPage);
  g('bg-drawer', surfaces.bgDrawer);
  g('text-drawer', surfaces.textDrawer, 'texto/ícones do menu sobre bg-drawer (temas escuros ≠ text-primary)');
  g('surface', surfaces.surface);

  sec('Slate neutral scale (uiTokens.slate)');
  for (const k of ['100', '200', '300', '400', '500', '600', '700']) g(`slate-${k}`, slate[k]);

  sec('Soft tints — status containers (uiTokens.softTints)');
  for (const [name, t] of Object.entries(softTints)) {
    g(`tint-${name}-bg`, t.bg);
    g(`tint-${name}-border`, t.border);
    g(`tint-${name}-fg`, t.fg);
    g(`tint-${name}-sub`, t.sub);
  }

  sec('WhatsApp brand greens (brandColors.whatsapp)');
  g('wa-main', brandColors.whatsapp.main);
  g('wa-action', brandColors.whatsapp.action);
  g('wa-bubble', brandColors.whatsapp.bubble);
  g('wa-chat-bg', brandColors.whatsapp.chatBg);
  g('wa-header', brandColors.whatsapp.header);

  sec('Typography (DM Sans corpo / Plus Jakarta Sans títulos)');
  g('font-family', fontFamily);
  g('font-heading', fontHeading);
  g('fw-regular', 400); g('fw-medium', 500); g('fw-bold', 700);
  for (const [k, v] of Object.entries(fontSizeVars)) g(`fs-${k}`, v);

  sec('Shape + elevation + spacing');
  g('radius-card', `${shape.radiusCard}px`);
  g('radius-card-mobile', `${shape.radiusCardMobile}px`);
  g('radius-sm', `${shape.radiusSm}px`);
  g('radius-lg', `${shape.radiusLg}px`);
  g('shadow-card', shape.shadowCard);
  g('shadow-hover', shape.shadowHover);
  for (let i = 1; i <= 4; i++) g(`space-${i}`, `${spacingBase * i}px`);

  return L.join('\n');
}

export { brandColors, softTints, slate };
